/** Typed banking endpoints (Phase 3 + 4). */
import { apiDownload, apiRequest, apiUpload } from '@/lib/api'
import type {
  AccountDto,
  AccountHealthDto,
  CardDto,
  FxConversionDto,
  FxQuoteDto,
  FxRatesDto,
  PreflightDto,
  TransactionDto,
  TransactionPageDto,
} from '@/lib/types'

function qs(params: Record<string, string | number | undefined>): string {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== '')
  return entries.length ? '?' + entries.map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`).join('&') : ''
}

export interface TransferPayload {
  account_id: string
  amount_cents: number
  currency: string
  counterparty_name: string
  counterparty_iban: string
  reference?: string
}

export const bankingApi = {
  // ── Redirect-hosted Swan onboarding (étape 12) ──
  startOnboarding: (kyc: {
    employment_status: string
    monthly_income: string
    is_united_states_person: boolean
    us_tax_id?: string
  }) =>
    apiRequest<
      | { eligible: true; consent_url: string; onboarding_id: string }
      | { eligible: false; reason: string }
    >('/onboarding/swan/start', { method: 'POST', body: kyc }),
  completeOnboarding: (body: { code: string; state: string }) =>
    apiRequest<{ account_id: string | null }>('/onboarding/swan/complete', {
      method: 'POST',
      body,
    }),

  listAccounts: () => apiRequest<{ items: AccountDto[] }>('/accounts'),
  getAccount: (id: string) => apiRequest<{ account: AccountDto }>(`/accounts/${id}`),
  getHealth: (id: string) => apiRequest<AccountHealthDto>(`/accounts/${id}/health`),

  listTransactions: (accountId: string, params: Record<string, string | number | undefined> = {}) =>
    apiRequest<TransactionPageDto>(`/accounts/${accountId}/transactions${qs(params)}`),
  getTransaction: (id: string) => apiRequest<{ transaction: TransactionDto }>(`/transactions/${id}`),
  exportTransactions: (accountId: string) =>
    apiDownload(`/accounts/${accountId}/transactions/export`),

  initiateTransfer: (body: TransferPayload) =>
    apiRequest<{ transfer_id: string; requires_confirmation: boolean }>('/transfers', {
      method: 'POST',
      body,
    }),
  confirmTransfer: (transferId: string, code: string) =>
    apiRequest<{ transaction_id: string; status: string }>(`/transfers/${transferId}/confirm`, {
      method: 'POST',
      body: { code },
    }),
  cancelTransfer: (transferId: string) =>
    apiRequest<void>(`/transfers/${transferId}/cancel`, { method: 'POST' }),

  listCards: (accountId: string) => apiRequest<{ items: CardDto[] }>(`/accounts/${accountId}/cards`),
  issueCard: (accountId: string, spendingLimitCents: number) =>
    apiRequest<{ card: CardDto }>(`/accounts/${accountId}/cards`, {
      method: 'POST',
      body: { spending_limit_cents: spendingLimitCents },
    }),
  getCard: (id: string) => apiRequest<{ card: CardDto }>(`/cards/${id}`),
  freezeCard: (id: string) => apiRequest<{ card: CardDto }>(`/cards/${id}/freeze`, { method: 'POST' }),
  unfreezeCard: (id: string) =>
    apiRequest<{ card: CardDto }>(`/cards/${id}/unfreeze`, { method: 'POST' }),
  updateCardLimits: (id: string, perMonthCents: number) =>
    apiRequest<{ card: CardDto }>(`/cards/${id}/limits`, {
      method: 'PATCH',
      body: { per_month_cents: perMonthCents },
    }),
  cancelCard: (id: string) => apiRequest<{ card: CardDto }>(`/cards/${id}/cancel`, { method: 'POST' }),

  // ── Multi-IBAN (Phase 4) ──
  addIban: () => apiRequest<{ account: AccountDto }>('/accounts/additional', { method: 'POST' }),
  setPreferredIban: (id: string) =>
    apiRequest<void>(`/accounts/${id}/set-preferred`, { method: 'PATCH' }),

  // ── FX (Phase 4, USP #3) ──
  fxRates: () => apiRequest<FxRatesDto>('/fx/rates'),
  fxQuote: (base: string, quote: string) =>
    apiRequest<FxQuoteDto>(`/fx/quote${qs({ base, quote })}`),
  convert: (body: {
    source_account_id: string
    destination_account_id: string
    source_amount_cents: number
  }) => apiRequest<{ conversion: FxConversionDto }>('/fx/conversions', { method: 'POST', body }),
  fxHistory: () => apiRequest<{ items: FxConversionDto[] }>('/fx/conversions/history'),

  // ── Pre-flight check (Phase 4, USP #2) ──
  preflight: (body: { amount_cents: number; counterparty_iban: string }) =>
    apiRequest<PreflightDto>('/transfers/preflight', { method: 'POST', body }),
  uploadJustification: (assessmentId: string, file: File, note?: string) => {
    const form = new FormData()
    if (note) form.append('note', note)
    form.append('file', file)
    return apiUpload<{ uploaded: boolean }>(`/transfers/${assessmentId}/justification`, form)
  },
}
